import { useState, useEffect } from 'react'
import type { Session } from '@supabase/supabase-js'
import type { Booking } from '../../lib/types'
import { rebook, type RebookResponse } from '../../lib/admin-api'
import { ModalDialog } from '../ui/ModalDialog'
import { format } from 'date-fns'
import { SectionLabel, sectionLabelVariants, Button, Spinner } from '@perch/ui'

/**
 * Record Rebook Confirmation Preview Dialog.
 *
 * "Record Rebook" means: the admin has ALREADY manually rebooked on the airline
 * website. This dialog records the result in the system — updates the booking
 * baseline, calculates commission, charges the user's card, sends a confirmation
 * email, and resolves the Linear issue.
 *
 * All side effects are non-blocking: the rebook succeeds even if Stripe, email,
 * or Linear fail. The result screen shows the status of each side effect.
 */
export function RecordRebookDialog({ booking, session, onClose, onRebooked }: {
  booking: Booking
  session: Session
  onClose: () => void
  onRebooked: (bookingId: string) => void
}) {
  // Default new_price from linear issue drop_price or latest_price
  const defaultPrice = booking.linear_issue?.drop_price ?? booking.latest_price ?? 0
  const originalPrice = booking.is_award_booking
    ? (booking.effective_points_used ?? booking.points_used ?? 0)
    : (booking.effective_price_paid ?? booking.total_price_paid ?? 0)

  const [newPrice, setNewPrice] = useState(String(defaultPrice))
  const [confirming, setConfirming] = useState(false)
  const [result, setResult] = useState<RebookResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const numericPrice = parseFloat(newPrice) || 0
  const savings = originalPrice - numericPrice
  const savingsPercent = originalPrice > 0 ? Math.round((savings / originalPrice) * 100) : 0

  // Commission estimate (client-side, 15% rate, $25 minimum for cash)
  const COMMISSION_RATE = 0.15
  const MIN_THRESHOLD = 25
  let commissionAmount = 0
  let commissionNote = ''
  if (booking.is_award_booking) {
    commissionNote = 'No commission (award booking)'
  } else if (savings < MIN_THRESHOLD) {
    commissionNote = `No commission (below $${MIN_THRESHOLD} threshold)`
  } else if (savings <= 0) {
    commissionNote = 'No commission (no savings)'
  } else {
    commissionAmount = Math.round(savings * COMMISSION_RATE * 100) / 100
    commissionNote = ''
  }
  const netSavings = savings - commissionAmount

  const isValid = numericPrice > 0
  const isHigherThanOriginal = numericPrice > originalPrice

  const segments = booking.segments || []
  const firstSeg = segments[0]
  const lastSeg = segments[segments.length - 1]
  const route = firstSeg && lastSeg ? `${firstSeg.origin} → ${lastSeg.destination}` : 'N/A'
  const dateStr = firstSeg ? format(new Date(firstSeg.departure_datetime), 'MMM d, yyyy') : '—'

  // Cmd+Enter to confirm — Escape / click-outside are handled by ModalDialog.
  // Backdrop-close is disabled while the success result is showing so a
  // stray click doesn't dismiss the summary before the admin reads it.
  useEffect(() => {
    function handleKeydown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && isValid && !confirming && !result) {
        handleConfirm()
      }
    }
    document.addEventListener('keydown', handleKeydown)
    return () => document.removeEventListener('keydown', handleKeydown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid, confirming, result, newPrice])

  async function handleConfirm() {
    setConfirming(true)
    setError(null)
    try {
      const res = await rebook(session, {
        booking_id: booking.id,
        new_price: numericPrice,
        original_price: originalPrice,
      })
      setResult(res)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rebook failed')
      setConfirming(false)
    }
  }

  const formatPrice = (amount: number) => {
    if (booking.is_award_booking) return `${amount.toLocaleString()} pts`
    return `$${amount.toLocaleString()}`
  }

  return (
    <ModalDialog
      onClose={onClose}
      maxWidth="lg"
      closeOnBackdrop={!result}
      header={
        <>
          <span className="text-lg text-stone-900 dark:text-stone-100">Record Rebook</span>
          <span className="font-mono text-sm text-stone-500">{booking.airline_code} {booking.pnr}</span>
        </>
      }
    >
      <div className="p-6 space-y-5">
          {result ? (
            /* --- Success Result --- */
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="font-data text-lg tabular-nums text-emerald-600 dark:text-emerald-400">
                    Rebooked at {formatPrice(numericPrice)}
                  </p>
                  <p className="text-sm text-stone-500">
                    {route} · {dateStr}
                  </p>
                </div>
              </div>

              <div className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-emerald-500">&#10003;</span>
                  <span className="text-stone-700 dark:text-stone-300">Savings: {formatPrice(savings)} ({savingsPercent}%)</span>
                </div>

                <div className="flex items-center gap-2">
                  {result.charge_status === 'collected' ? (
                    <>
                      <span className="text-emerald-500">&#10003;</span>
                      <span className="text-stone-700 dark:text-stone-300">
                        Commission: ${result.commission?.commissionAmount?.toFixed(2)} (collected)
                      </span>
                    </>
                  ) : result.charge_status === 'pending' ? (
                    <>
                      <span className="text-amber-500">&#9719;</span>
                      <span className="text-amber-700 dark:text-amber-400">
                        Commission: pending — {result.commission?.commissionAmount ? `$${result.commission.commissionAmount.toFixed(2)}` : 'calculating'}
                      </span>
                    </>
                  ) : result.charge_status === 'not_applicable' || result.charge_status === 'below_threshold' ? (
                    <>
                      <span className="text-stone-400">—</span>
                      <span className="text-stone-500">No commission ({result.charge_status === 'not_applicable' ? 'award booking' : 'below threshold'})</span>
                    </>
                  ) : (
                    <>
                      <span className="text-stone-400">—</span>
                      <span className="text-stone-500">Commission: {result.charge_status}</span>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {result.notification_sent ? (
                    <>
                      <span className="text-emerald-500">&#10003;</span>
                      <span className="text-stone-700 dark:text-stone-300">Email: sent to user</span>
                    </>
                  ) : (
                    <>
                      <span className="text-red-500">&#10007;</span>
                      <span className="text-red-600 dark:text-red-400">Email: not sent</span>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {booking.linear_issue ? (
                    <>
                      <span className="text-emerald-500">&#10003;</span>
                      <span className="text-stone-700 dark:text-stone-300">
                        Linear #{booking.linear_issue.linear_issue_number}: resolved
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-stone-400">—</span>
                      <span className="text-stone-500">Linear: no issue</span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={() => onRebooked(booking.id)} size="sm">
                  Done
                </Button>
              </div>
            </div>
          ) : (
            /* --- Preview / Confirm --- */
            <>
              <div>
                <SectionLabel className="mb-2">Booking</SectionLabel>
                <div className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-stone-900 dark:text-stone-100">{route}</span>
                      <span className="text-stone-400 text-sm ml-2">· {dateStr}</span>
                    </div>
                    <span className="text-xs text-stone-400">{booking.user_name}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <SectionLabel>Original Price</SectionLabel>
                  <p className="text-lg font-data tabular-nums text-stone-600 dark:text-stone-400 mt-1">
                    {formatPrice(originalPrice)}
                  </p>
                </div>
                <div>
                  <label htmlFor="new-price" className={sectionLabelVariants()}>
                    New Price
                  </label>
                  <div className="relative mt-1">
                    {!booking.is_award_booking && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 font-data">$</span>
                    )}
                    <input
                      id="new-price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={newPrice}
                      onChange={(e) => setNewPrice(e.target.value)}
                      className={`w-full ${booking.is_award_booking ? 'pl-3' : 'pl-7'} pr-3 py-2 text-lg font-data tabular-nums bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50`}
                      autoFocus
                    />
                  </div>
                </div>
              </div>

              {isHigherThanOriginal && (
                <div className="px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-sm">
                  New price is higher than original — no savings.
                </div>
              )}

              {savings > 0 && (
                <div className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-3 space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-500">Gross Savings</span>
                    <span className="font-data tabular-nums text-emerald-600 dark:text-emerald-400">{formatPrice(savings)} ({savingsPercent}%)</span>
                  </div>
                  {commissionAmount > 0 ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-stone-500">Commission (15%)</span>
                        <span className="font-data tabular-nums text-stone-700 dark:text-stone-300">${commissionAmount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between border-t border-stone-200 dark:border-stone-700 pt-1.5">
                        <span className="text-stone-500">Net to User</span>
                        <span className="font-data tabular-nums font-medium text-emerald-600 dark:text-emerald-400">${netSavings.toFixed(2)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between text-stone-400">
                      <span>{commissionNote}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="text-xs text-stone-400 dark:text-stone-500 space-y-0.5">
                <p>&#10003; Update booking baseline to new price</p>
                <p>&#10003; Send rebook confirmation email to user</p>
                {commissionAmount > 0 && <p>&#10003; Charge commission to user's card on file</p>}
                {booking.linear_issue && <p>&#10003; Resolve Linear issue #{booking.linear_issue.linear_issue_number}</p>}
              </div>

              {error && (
                <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-stone-200 dark:border-stone-800">
                <Button onClick={onClose} disabled={confirming} variant="ghost" size="sm">
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={!isValid || confirming}
                  size="sm"
                  variant="emerald"
                >
                  {confirming ? (
                    <span className="flex items-center gap-2">
                      <Spinner />
                      Recording...
                    </span>
                  ) : (
                    <>Confirm Rebook <span className="text-emerald-300 ml-1 text-xs">&#8984;&#9166;</span></>
                  )}
                </Button>
              </div>
            </>
          )}
      </div>
    </ModalDialog>
  )
}
